/**
 * rgPush — Web Push subscription helper (admin only).
 *
 * Usage :
 *   import { enableAdminPush, disableAdminPush, getPushSupport, currentSubscription } from "@/lib/rgPush";
 *
 * Notes :
 * - HTTPS requis (marche en dev via localhost).
 * - iOS Safari : uniquement en PWA installée (add to home screen).
 * - Le bouton d'activation doit être déclenché par un gesture utilisateur.
 */
import { rgApi } from "./rgApi";

const SW_PATH = "/rg-sw.js";
const SW_SCOPE = "/";

function urlB64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function getPushSupport() {
  const supported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;
  return {
    supported,
    permission: supported ? Notification.permission : "unsupported",
  };
}

async function ensureRegistration() {
  const existing = await navigator.serviceWorker.getRegistration(SW_SCOPE);
  if (existing) return existing;
  return navigator.serviceWorker.register(SW_PATH, { scope: SW_SCOPE });
}

export async function currentSubscription() {
  const { supported } = getPushSupport();
  if (!supported) return null;
  const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE);
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function enableAdminPush() {
  const { supported } = getPushSupport();
  if (!supported) throw new Error("Notifications push non supportées sur ce navigateur.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Permission refusée. Autorisez les notifications dans les réglages du navigateur.");
  }
  const reg = await ensureRegistration();
  await navigator.serviceWorker.ready;
  const { publicKey } = await rgApi.pushPublicKey();
  if (!publicKey) throw new Error("Clé VAPID publique indisponible côté serveur.");
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(publicKey),
    });
  }
  await rgApi.pushSubscribe(sub.toJSON());
  return sub;
}

export async function disableAdminPush() {
  const sub = await currentSubscription();
  if (!sub) return { ok: true, unsubscribed: false };
  try {
    await rgApi.pushUnsubscribe(sub.toJSON());
  } catch (_) {
    /* on continue le côté navigateur même si backend échoue */
  }
  await sub.unsubscribe();
  return { ok: true, unsubscribed: true };
}
